import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// A contenteditable input where `{{var}}` tokens are rendered as atomic
// chips. Typing, selecting, and backspace treat each chip as a single
// indivisible character. Use this for any field where variables should
// look + behave like tags.
//
// Behaviour contract:
//   • value is the canonical string ("https://{{HOST}}/users").
//   • External value changes re-render the DOM. User typing does NOT —
//     we serialise the DOM back to a string and call onChange.
//   • Backspace right after a chip removes the whole chip.
//   • Typing `@` (when `mentionable` true) or `{{` opens an autosuggest
//     popover; picking inserts a chip atomically.
//   • Pressing Enter (single-line) blurs + fires onEnter when given;
//     otherwise inserts a newline (multiline).
//
// We deliberately keep this small — no rich-text editor library. The DOM
// shape is just text nodes and `<span class="chip" data-name>…</span>`
// elements, which keeps reasoning about caret positioning straightforward.

export interface Suggestion {
  /** The token to insert (e.g., "HOST" → renders {{HOST}}). */
  name: string;
  /** Optional secondary line shown in the popover. */
  hint?: string;
  /** Optional left-side icon (e.g., user avatar) in the popover row. */
  leading?: React.ReactNode;
}

export type TokenKind = "var" | "mention";

interface Props {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
  /** All variable names known in scope (env + referenced elsewhere). */
  suggestions: Suggestion[];
  /** When true, `Enter` blurs + calls onEnter; otherwise a newline. */
  multiline?: boolean;
  rows?: number;
  onEnter?: () => void;
  /** ⌘↩ / Ctrl↩ regardless of multiline — used to post comments etc. */
  onSubmit?: () => void;
  spellCheck?: boolean;
  "aria-label"?: string;
  /** Which kind of token this input handles. Defaults to "var". */
  kind?: TokenKind;
  /** Optional predicate — if it returns false, the chip is rendered with
   *  the "is-missing" treatment (red, ⚠ icon). Used to flag variables
   *  that don't exist in the active environment. */
  isResolved?: (name: string) => boolean;
  /** Optional value lookup — if a chip's variable is defined, return
   *  its value so we can surface it as a tooltip on hover. */
  resolveValue?: (name: string) => string | undefined;
}

const ZWSP = "​";

// Per-kind spec: serialised form, parser regex, autosuggest triggers,
// chip CSS class.
interface KindSpec {
  /** Serialise a token name → string form (e.g., "HOST" → "{{HOST}}"). */
  serialise: (name: string) => string;
  /** Regex (global) for finding embedded tokens in a value string. */
  tokenRe: RegExp;
  /** CSS class used on the chip span. */
  chipClass: string;
  /** Regex for the partial-pattern shown in popover (always opens). */
  triggerRe: RegExp;
  /** Regex for a bare-word trigger that matches a known name. Null disables. */
  wordRe: RegExp | null;
  /** Length of the serialised form for caret math. */
  serialisedLen: (name: string) => number;
}

function prefixLen(kind: TokenKind): number {
  return kind === "var" ? 2 : 1;
}

const SPECS: Record<TokenKind, KindSpec> = {
  var: {
    serialise: (n) => `{{${n}}}`,
    tokenRe: /\{\{([a-zA-Z0-9_.-]+)\}\}/g,
    chipClass: "var-chip",
    triggerRe: /\{\{([a-zA-Z0-9_.-]*)$/,
    wordRe: /([A-Za-z_][A-Za-z0-9_]*)$/,
    serialisedLen: (n) => n.length + 4,
  },
  mention: {
    serialise: (n) => `@${n}`,
    tokenRe: /(?:^|(?<=\W))@([A-Za-z0-9_\-\.]+)/g,
    chipClass: "mention-chip",
    triggerRe: /(?:^|\W)@([A-Za-z0-9_\-\.]*)$/,
    wordRe: null,
    serialisedLen: (n) => n.length + 1,
  },
};

// Build the inner HTML for a value string. Text segments become text
// nodes; tokens become non-editable chip spans. A zero-width space sits
// after every chip so the caret can land *after* it (browsers won't put
// the caret directly behind a contenteditable=false element).
function valueToNodes(
  value: string,
  spec: KindSpec,
  isResolved?: (n: string) => boolean,
  resolveValue?: (n: string) => string | undefined,
): Node[] {
  const out: Node[] = [];
  let last = 0;
  const re = new RegExp(spec.tokenRe.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) {
    if (m.index > last) out.push(document.createTextNode(value.slice(last, m.index)));
    const name = m[1];
    const missing = !!isResolved && !isResolved(name);
    const resolved = resolveValue?.(name);
    out.push(makeChip(name, spec.chipClass, missing, resolved));
    out.push(document.createTextNode(ZWSP));
    last = m.index + m[0].length;
  }
  if (last < value.length) out.push(document.createTextNode(value.slice(last)));
  return out;
}

function makeChip(name: string, cls: string, missing = false, value?: string): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = cls + (missing ? " is-missing" : "");
  span.contentEditable = "false";
  span.setAttribute("data-name", name);
  // `data-tooltip` drives the custom CSS tooltip (styled bubble that
  // appears on hover, faster than the native title). Keep `title`
  // empty so the browser doesn't double-tip.
  if (missing) {
    span.setAttribute("data-tooltip", `Unresolved · ${name}`);
  } else if (value !== undefined && value !== "") {
    span.setAttribute("data-tooltip", `${name} = ${value}`);
  } else {
    span.setAttribute("data-tooltip", name);
  }
  span.textContent = name;
  return span;
}

// Serialise the contenteditable's DOM back into a canonical string.
function nodesToValue(root: HTMLElement, spec: KindSpec): string {
  let out = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? "").replace(/​/g, "");
    } else if (node instanceof HTMLElement) {
      if (node.dataset.name) {
        out += spec.serialise(node.dataset.name);
      } else if (node.tagName === "BR") {
        out += "\n";
      } else {
        for (const c of Array.from(node.childNodes)) walk(c);
      }
    }
  };
  for (const c of Array.from(root.childNodes)) walk(c);
  return out;
}

// Save the caret as a numeric offset into the SERIALISED value, so we
// can restore it after re-rendering the DOM from a new value.
function getCaretOffset(root: HTMLElement, spec: KindSpec): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  const pre = range.cloneRange();
  pre.selectNodeContents(root);
  pre.setEnd(range.endContainer, range.endOffset);
  let len = 0;
  const inSel = pre.cloneContents();
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      len += (node.textContent ?? "").replace(/​/g, "").length;
    } else if (node instanceof HTMLElement) {
      if (node.dataset.name) len += spec.serialisedLen(node.dataset.name);
      else if (node.tagName === "BR") len += 1;
      else for (const c of Array.from(node.childNodes)) walk(c);
    }
  };
  for (const c of Array.from(inSel.childNodes)) walk(c);
  return len;
}

function setCaretOffset(root: HTMLElement, target: number, spec: KindSpec) {
  let remaining = target;
  let placed = false;
  const range = document.createRange();
  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = (node.textContent ?? "");
      const cleanLen = raw.replace(/​/g, "").length;
      if (remaining <= cleanLen) {
        let domOffset = 0;
        let counted = 0;
        for (let i = 0; i < raw.length; i++) {
          if (counted === remaining) break;
          if (raw[i] !== ZWSP) counted++;
          domOffset = i + 1;
        }
        range.setStart(node, domOffset);
        range.collapse(true);
        placed = true;
        return true;
      }
      remaining -= cleanLen;
      return false;
    }
    if (node instanceof HTMLElement) {
      if (node.dataset.name) {
        const len = spec.serialisedLen(node.dataset.name);
        if (remaining < len) {
          const parent = node.parentNode!;
          const idx = Array.from(parent.childNodes).indexOf(node);
          range.setStart(parent, idx);
          range.collapse(true);
          placed = true;
          return true;
        }
        remaining -= len;
        return false;
      }
      if (node.tagName === "BR") {
        if (remaining <= 0) {
          const parent = node.parentNode!;
          const idx = Array.from(parent.childNodes).indexOf(node);
          range.setStart(parent, idx);
          range.collapse(true);
          placed = true;
          return true;
        }
        remaining -= 1;
        return false;
      }
      for (const c of Array.from(node.childNodes)) if (walk(c)) return true;
    }
    return false;
  };
  for (const c of Array.from(root.childNodes)) if (walk(c)) break;
  if (!placed) {
    range.selectNodeContents(root);
    range.collapse(false);
  }
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}

export default function ChipInput({
  value, onChange, className = "", placeholder, suggestions, multiline = false, rows = 4, onEnter, onSubmit, spellCheck = false,
  kind = "var",
  isResolved,
  resolveValue,
  ...rest
}: Props) {
  const ariaLabel = rest["aria-label"];
  const ref = useRef<HTMLDivElement | null>(null);
  const spec = SPECS[kind];
  const lastWritten = useRef("");
  const lastResolver = useRef<typeof isResolved>(undefined);
  const lastValueLookup = useRef<typeof resolveValue>(undefined);
  const [suggest, setSuggest] = useState<{ query: string; mode: "trigger" | "word"; rect: DOMRect | null } | null>(null);
  const [hover, setHover] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      value === lastWritten.current &&
      isResolved === lastResolver.current &&
      resolveValue === lastValueLookup.current
    ) return;
    const savedCaret = el.contains(document.activeElement) ? getCaretOffset(el, spec) : null;
    el.innerHTML = "";
    for (const n of valueToNodes(value, spec, isResolved, resolveValue)) el.appendChild(n);
    if (!el.childNodes.length) el.appendChild(document.createTextNode(""));
    lastWritten.current = value;
    lastResolver.current = isResolved;
    lastValueLookup.current = resolveValue;
    if (savedCaret != null) setCaretOffset(el, Math.min(savedCaret, value.length), spec);
  }, [value, spec, isResolved, resolveValue]);

  const allNames = useMemo(() => suggestions.map((s) => s.name), [suggestions]);

  // Detect a trigger right before the caret. Two flavours:
  // "trigger" (explicit prefix like `{{` or `@`) always shows; "word"
  // (bare prefix matching a known name) only when there'd be a match.
  const computeSuggest = (): { query: string; mode: "trigger" | "word" } | null => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return null;
    if (!ref.current || !ref.current.contains(range.endContainer)) return null;
    const node = range.endContainer;
    if (node.nodeType !== Node.TEXT_NODE) return null;
    const before = (node.textContent ?? "").slice(0, range.endOffset).replace(/​/g, "");
    const t = spec.triggerRe.exec(before);
    if (t) return { query: t[1], mode: "trigger" };
    if (spec.wordRe) {
      const w = spec.wordRe.exec(before);
      if (w) {
        const ql = w[1].toLowerCase();
        const hit = allNames.some((n) => n.toLowerCase().startsWith(ql));
        if (hit) return { query: w[1], mode: "word" };
      }
    }
    return null;
  };

  const updateSuggest = () => {
    const s = computeSuggest();
    if (!s) { setSuggest(null); return; }
    // Get the caret rect so the popover can position relative to it.
    const sel = window.getSelection();
    const rect = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).getBoundingClientRect() : null;
    setSuggest({ ...s, rect: rect ?? null });
  };

  const filtered = useMemo(() => {
    if (!suggest) return [] as Suggestion[];
    const q = suggest.query.toLowerCase();
    const exact = suggestions.filter((s) => s.name.toLowerCase().startsWith(q));
    if (q.length === 0) return suggestions.slice(0, 20);
    const fuzzy = suggestions.filter((s) =>
      !s.name.toLowerCase().startsWith(q) && s.name.toLowerCase().includes(q),
    );
    return [...exact, ...fuzzy].slice(0, 20);
  }, [suggestions, suggest]);

  useEffect(() => { setHover(0); }, [suggest?.query]);

  const pickSuggestion = (name: string) => {
    const el = ref.current;
    if (!el || !suggest) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const node = range.endContainer;
    if (node.nodeType !== Node.TEXT_NODE) return;
    const text = node as Text;
    const raw = text.textContent ?? "";
    const caret = range.endOffset;
    const before = raw.slice(0, caret);
    // Match either the explicit trigger or the bare word, whichever the
    // user is currently in.
    const partialRe = suggest.mode === "trigger" ? spec.triggerRe : spec.wordRe;
    if (!partialRe) return;
    const m = partialRe.exec(before);
    if (!m) return;
    // For triggers like `(?:^|\W)@…` the lookbehind isn't captured but
    // sits in front of the match. The capture group is what we replace.
    const captureStart = before.length - m[1].length - (suggest.mode === "trigger" ? prefixLen(kind) : 0);
    const start = Math.max(captureStart, 0);
    const beforeText = raw.slice(0, start);
    const afterText = raw.slice(caret);
    text.textContent = beforeText;
    const chip = makeChip(name, spec.chipClass, !!isResolved && !isResolved(name), resolveValue?.(name));
    // Append a trailing space so the user can keep typing immediately
    // after the chip — but only for mentions in comments. For variable
    // chips inside URLs/headers/etc., a space would be wrong (e.g.
    // `{{HOST}}/path` should stay glued).
    const needsSpace = kind === "mention" && !afterText.startsWith(" ");
    const tail = ZWSP + (needsSpace ? " " : "") + afterText;
    const after = document.createTextNode(tail);
    text.parentNode!.insertBefore(after, text.nextSibling);
    text.parentNode!.insertBefore(chip, after);
    const newRange = document.createRange();
    // Land the caret after the ZWSP + space (or just after the ZWSP if
    // we didn't add one) so typing continues to the right of the chip.
    newRange.setStart(after, needsSpace ? 2 : 1);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    setSuggest(null);
    const next = nodesToValue(el, spec);
    lastWritten.current = next;
    onChange(next);
  };

  const onInput = () => {
    const el = ref.current;
    if (!el) return;
    // Don't auto-promote the token the user is currently typing inside —
    // that's how `@a` was getting chipped after one letter and the caret
    // bouncing back to the start. We pass the caret's text node + offset
    // so promoteInlineChips can skip a match that ends exactly there.
    const sel = window.getSelection();
    let caretNode: Text | null = null;
    let caretOffset = 0;
    if (sel && sel.rangeCount > 0 && sel.getRangeAt(0).collapsed) {
      const r = sel.getRangeAt(0);
      if (r.endContainer.nodeType === Node.TEXT_NODE && el.contains(r.endContainer)) {
        caretNode = r.endContainer as Text;
        caretOffset = r.endOffset;
      }
    }
    promoteInlineChips(el, spec, isResolved, resolveValue, caretNode, caretOffset);
    const next = nodesToValue(el, spec);
    lastWritten.current = next;
    onChange(next);
    updateSuggest();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Autosuggest navigation, only when the popup is open.
    if (suggest && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault(); e.stopPropagation();
        setHover((h) => (h + 1) % filtered.length); return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault(); e.stopPropagation();
        setHover((h) => (h - 1 + filtered.length) % filtered.length); return;
      }
      if (e.key === "Home") {
        e.preventDefault(); e.stopPropagation();
        setHover(0); return;
      }
      if (e.key === "End") {
        e.preventDefault(); e.stopPropagation();
        setHover(filtered.length - 1); return;
      }
      if (e.key === "PageDown") {
        e.preventDefault(); e.stopPropagation();
        setHover((h) => Math.min(filtered.length - 1, h + 5)); return;
      }
      if (e.key === "PageUp") {
        e.preventDefault(); e.stopPropagation();
        setHover((h) => Math.max(0, h - 5)); return;
      }
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault(); e.stopPropagation();
        pickSuggestion(filtered[hover].name); return;
      }
      if (e.key === "Escape") {
        e.preventDefault(); e.stopPropagation();
        setSuggest(null); return;
      }
    }
    // Backspace right after a chip → delete the whole chip.
    if (e.key === "Backspace") {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!range.collapsed) return;
      const node = range.endContainer;
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node as Text;
        const off = range.endOffset;
        const slice = (text.textContent ?? "").slice(0, off);
        // The caret might be right after a ZWSP that follows a chip.
        // Skip the ZWSP and check whether the prior sibling is a chip.
        const onlyZWSPs = slice.replace(/​/g, "") === "";
        if (slice === ZWSP || onlyZWSPs) {
          const prev = text.previousSibling;
          if (prev instanceof HTMLElement && prev.dataset.name) {
            e.preventDefault();
            const parent = text.parentNode!;
            const anchor = prev.previousSibling;
            parent.removeChild(prev);
            text.textContent = (text.textContent ?? "").replace(/^​+/, "");
            const newRange = document.createRange();
            if (anchor && anchor.nodeType === Node.TEXT_NODE) {
              newRange.setStart(anchor, (anchor.textContent ?? "").length);
            } else {
              newRange.setStart(text, 0);
            }
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
            const next = nodesToValue(ref.current!, spec);
            lastWritten.current = next;
            onChange(next);
            updateSuggest();
            return;
          }
        }
      }
    }
    // ⌘↩ / Ctrl↩ → submit (multiline or single).
    if (onSubmit && (e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSubmit();
      return;
    }
    // Plain Enter on single-line: fire onEnter (e.g., send the request).
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      if (onEnter) onEnter();
      return;
    }
  };

  // Paste plain text — never let the browser drop HTML in.
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  // The empty state shows a placeholder. Track whether the editable is
  // actually empty (ignoring ZWSPs that might linger).
  const [empty, setEmpty] = useState(value.length === 0);
  useEffect(() => { setEmpty(value.length === 0); }, [value]);

  const minHeight = multiline ? rows * 20 : undefined;

  // Custom hover tooltip — when the pointer crosses onto any chip
  // inside this editor (chips carry a `data-tooltip` attribute) we
  // show a fixed-position bubble anchored to the chip's rect. Faster
  // than the native title (no 500ms delay), and not clipped by the
  // input's overflow-x.
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);
  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as Element | null)?.closest?.("[data-tooltip]");
    if (target instanceof HTMLElement) {
      const text = target.getAttribute("data-tooltip");
      if (!text) { setTooltip(null); return; }
      const r = target.getBoundingClientRect();
      setTooltip({ text, x: r.left + r.width / 2, y: r.top });
    } else {
      setTooltip(null);
    }
  };

  return (
    <div className={"chip-input-wrap relative w-full " + (multiline ? "" : "")}>
      <div
        ref={ref}
        role="textbox"
        contentEditable
        suppressContentEditableWarning
        spellCheck={spellCheck}
        aria-label={ariaLabel}
        aria-multiline={multiline || undefined}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => setTimeout(() => setSuggest(null), 80)}
        onFocus={() => updateSuggest()}
        onMouseMove={onMouseMove}
        onMouseLeave={() => setTooltip(null)}
        onMouseDownCapture={(e) => {
          // Intercept mousedown BEFORE the contenteditable receives it
          // so the browser doesn't move the caret to the click point,
          // steal focus away to the chip span, or collapse the existing
          // selection. The actual env-modal open still happens on
          // click below — this just stops the side-effects.
          if (kind !== "var") return;
          const t = e.target as HTMLElement | null;
          const chip = t?.closest?.(".var-chip") as HTMLElement | null;
          if (!chip) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          // Click on a {{NAME}} chip → open the env modal at that
          // variable. Mentions stay click-through to their normal
          // selection behavior.
          if (kind !== "var") return;
          const t = e.target as HTMLElement | null;
          const chip = t?.closest?.(".var-chip") as HTMLElement | null;
          if (!chip) return;
          const name = chip.getAttribute("data-name");
          if (!name) return;
          e.preventDefault();
          e.stopPropagation();
          const fn = (window as unknown as { __openEnvs?: (name?: string) => void }).__openEnvs;
          if (fn) fn(name);
        }}
        style={{ minHeight }}
        className={
          "chip-input input " +
          (multiline ? "whitespace-pre-wrap break-words " : "single-line ") +
          className
        }
      />
      {tooltip && createPortal(
        <div className="chip-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.text}
        </div>,
        document.body,
      )}
      {empty && placeholder && (
        <div className={
          "chip-input-placeholder pointer-events-none absolute left-3 right-3 text-text-faint select-none " +
          (multiline ? "top-2 truncate" : "top-1/2 -translate-y-1/2 truncate")
        }>
          {placeholder}
        </div>
      )}
      {suggest && filtered.length > 0 && createPortal(
        <SuggestPopover
          rect={suggest.rect}
          kind={kind}
          hover={hover}
          items={filtered}
          onPick={pickSuggestion}
          onHover={setHover}
        />,
        document.body,
      )}
    </div>
  );
}

// Portaled autosuggest popover. Positioned at fixed coordinates derived
// from the live caret rect so it escapes any overflow:hidden ancestor
// (the params / headers panel scrolls, the env modal etc.).
function SuggestPopover({
  rect, kind, hover, items, onPick, onHover,
}: {
  rect: DOMRect | null;
  kind: TokenKind;
  hover: number;
  items: Suggestion[];
  onPick: (name: string) => void;
  onHover: (i: number) => void;
}) {
  // Prefer to drop the popover below the caret; if there's not enough
  // room below, flip it above. Width clamps to the viewport.
  const above = kind === "mention";
  const width = 288;
  const padding = 6;
  const top = rect ? (above ? rect.top : rect.bottom) : 0;
  const left = rect ? Math.max(padding, Math.min(window.innerWidth - width - padding, rect.left)) : padding;
  const style: React.CSSProperties = {
    position: "fixed",
    left,
    top,
    transform: above ? "translateY(calc(-100% - 4px))" : "translateY(4px)",
    width,
    zIndex: 200,
  };
  // Keep the highlighted row scrolled into view as the user arrows
  // through the list — otherwise long popovers leave the selection
  // off-screen.
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [hover]);
  return (
    <div
      role="listbox"
      style={style}
      className="card shadow-[var(--shadow-strong)] text-sm overflow-hidden"
    >
      <div className="p-1 max-h-64 overflow-y-auto scroll-pane">
        {items.map((s, i) => (
          <button
            key={s.name}
            type="button"
            role="option"
            aria-selected={i === hover}
            ref={i === hover ? activeRef : undefined}
            onMouseDown={(e) => { e.preventDefault(); onPick(s.name); }}
            onMouseEnter={() => onHover(i)}
            className={
              "w-full text-left px-2 py-1.5 rounded flex items-center gap-2 " +
              (i === hover ? "bg-surface-2" : "")
            }
          >
            {s.leading}
            <span className="flex-1 min-w-0 truncate">{s.name}</span>
            {s.hint && <span className="muted-strong text-[11px] truncate max-w-[160px]">{s.hint}</span>}
          </button>
        ))}
      </div>
      <div className="border-t border-border px-2 py-1 flex items-center gap-3 text-[10px] muted-strong">
        <span><kbd className="kbd">↑</kbd> <kbd className="kbd">↓</kbd> nav</span>
        <span><kbd className="kbd">↵</kbd> insert</span>
        <span><kbd className="kbd">esc</kbd> close</span>
      </div>
    </div>
  );
}

// Scan the editable's text nodes for inline token literals the user typed
// directly and promote them to chip spans. Caret is preserved on the
// trailing text segment. If `caretNode`/`caretOffset` are supplied, any
// match that ends exactly at the caret position is skipped — that's the
// token the user is currently typing (we don't want `@a` chipping after
// one letter while the user wanted to type `@alice`).
function promoteInlineChips(
  root: HTMLElement,
  spec: KindSpec,
  isResolved?: (n: string) => boolean,
  resolveValue?: (n: string) => string | undefined,
  caretNode?: Text | null,
  caretOffset?: number,
) {
  const candidates: Text[] = [];
  const re = new RegExp(spec.tokenRe.source, "g");
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      re.lastIndex = 0;
      if (re.test(node.textContent ?? "")) candidates.push(node as Text);
    } else {
      for (const c of Array.from(node.childNodes)) walk(c);
    }
  };
  for (const c of Array.from(root.childNodes)) walk(c);
  for (const text of candidates) {
    const raw = text.textContent ?? "";
    const re2 = new RegExp(spec.tokenRe.source, "g");
    let last = 0;
    const frag = document.createDocumentFragment();
    let m: RegExpExecArray | null;
    let mutated = false;
    while ((m = re2.exec(raw)) !== null) {
      const end = m.index + m[0].length;
      // Skip the match the user is actively typing inside.
      if (caretNode === text && caretOffset === end) continue;
      mutated = true;
      if (m.index > last) frag.appendChild(document.createTextNode(raw.slice(last, m.index)));
      const missing = !!isResolved && !isResolved(m[1]);
      frag.appendChild(makeChip(m[1], spec.chipClass, missing, resolveValue?.(m[1])));
      frag.appendChild(document.createTextNode(ZWSP));
      last = end;
    }
    if (!mutated) continue;
    if (last < raw.length) frag.appendChild(document.createTextNode(raw.slice(last)));
    text.replaceWith(frag);
  }
}
